import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    List,
    ListItem,
    Divider
} from '@mui/material';
import {
    Add as AddIcon
} from '@mui/icons-material';
import { notification } from "antd";
import { getResponseErrorMessage } from '@/utils/api-service';
import DefaultSettings from "@/components/adtag/DefaultSettings";
import CreateGroupPopup from "@/components/adtag/CreateGroupPopup";
import SettingsGroup from "@/components/adtag/SettingsGroup";

import api from "@/api/index.ts";

// Основной компонент приложения
const AdTag = () => {
    const [defaultSettings, setDefaultSettings] = useState({
        preroll: '',
        midroll: '',
        postroll: ''
    });
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createGroupPopupOpen, setCreateGroupPopupOpen] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            getDefaultSettings();
            getGroups();
        } catch (error) {
            console.error('Ошибка загрузки данных:', error);
            const messageFormatter = getResponseErrorMessage(error, 'Ошибка загрузки данных')
            notification.error({
                message: messageFormatter
            });
        } finally {
            setLoading(false);
        }
    };

    const getDefaultSettings = async () => {
        const { data } = await api.adtag.getDefaultSettings();
        setDefaultSettings(data?.data || { preroll: '', midroll: '', postroll: '' });
    };

    const getGroups = async () => {
        const { data } = await api.adtag.getGroups();
        setGroups(data?.data || []);
    };

    const handleAddGroup = () => {
        setCreateGroupPopupOpen(true);
    };

    const handleCreateGroup = (newGroups) => {
        getGroups();
    };

    const handleDeleteGroup = (groupId) => {
        setGroups(groups.filter(g => g.id !== groupId));
    };

    const handleUpdateGroup = (groupId, updatedGroup) => {
        setGroups(groups.map(g => g.id === groupId ? updatedGroup : g));
    };

    const handleAddDomain = (groupId, domainName) => {
        getGroups();
    };

    const handleRemoveDomain = (groupId, domainId) => {
        getGroups();
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
                <Typography>Загрузка...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Настройки групп
            </Typography>

            <DefaultSettings
                settings={defaultSettings}
                onSave={setDefaultSettings}
            />

            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                        Группы доменов ({groups.length})
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddGroup}
                    >
                        Добавить группу
                    </Button>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {groups.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            Нет добавленных групп
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddGroup}
                            sx={{ mt: 2 }}
                        >
                            Создать первую группу
                        </Button>
                    </Box>
                ) : (
                    <List>
                        {groups.map((group) => (
                            <ListItem key={group.id} disablePadding sx={{ mb: 2 }}>
                                <SettingsGroup
                                    group={group}
                                    onDelete={handleDeleteGroup}
                                    onUpdate={handleUpdateGroup}
                                    onAddDomain={handleAddDomain}
                                    onRemoveDomain={handleRemoveDomain}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>

            <CreateGroupPopup
                open={createGroupPopupOpen}
                onClose={() => setCreateGroupPopupOpen(false)}
                onCreate={handleCreateGroup}
            />
        </Container>
    );
};

export default AdTag;